import { Button, ConfigProvider, Spin } from 'antd';
import { useState } from 'react';
import { RiInformationFill } from 'react-icons/ri';
import KYCModal from './kyc.modal';
import UsageLimitModal from './usage-limit.modal';
import { AppProgressBarCircle } from '@components/app-progressbar-circle';
import {
  WorkspaceRateLimit,
  useCreateKycVerificationSessionMutation,
  useWorkspaceRateLimitsQuery,
  useworkspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { cacheOnly } from '@shared/constants';
import { getMaxUsedPercent, getParsedLimits } from './helper';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { useEffectiveThemeMode, type ThemeMode } from '@shared/store/theme.store';

// antd derives hover/active shades from these seeds in JS, so they must be hex: copies of the App.css red-100
// (KYC badge fill) and a neutral border gray per theme mode.
const badgeColors = {
  light: { kycFill: '#fee2e2', usageBorder: '#d1d1d1' },
  dark: { kycFill: '#7f1d1d', usageBorder: '#565a63' },
} satisfies Record<ThemeMode, Record<string, string>>;

export function KYCHeaderBadge() {
  const { data } = useWorkspaceRateLimitsQuery(cacheOnly);
  const { data: workspaceAuthMe } = useworkspaceAuthMeV2Query(cacheOnly);
  const [openModal, setOpenModal] = useState<'usage' | 'kyc' | null>(null);
  const { t } = useTranslation();
  const colors = badgeColors[useEffectiveThemeMode()];

  const hasWorkspaceRateLimits = !!data?.workspaceRateLimits?.data?.length;
  const isCurrentWorkspaceKycVerified = !!workspaceAuthMe?.workspaceAuthMeV2?.data?.kycVerified;

  const parsedLimits = getParsedLimits(data?.workspaceRateLimits?.data as WorkspaceRateLimit[]);
  const maxUsedPercent = getMaxUsedPercent(parsedLimits);

  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [createKycVerificationSession, { loading: isCreatingKycSession }] = useCreateKycVerificationSessionMutation();
  const handleVerifyKYC = () => {
    createKycVerificationSession().then((res) => {
      const url = res.data?.createKycVerificationSession?.data?.sessionUrl ?? null;
      setSessionUrl(url);
      setOpenModal('kyc');
    });
  };
  const kycBadge = (
    <ConfigProvider
      theme={{
        token: {
          colorError: colors.kycFill,
        },
      }}
    >
      <Button
        danger
        type="primary"
        className="rounded-4xl! text-text-800! p-0! size-9! md:h-10! md:w-auto! md:px-2.5! gap-1.5!"
        onClick={handleVerifyKYC}
        disabled={isCreatingKycSession}
      >
        {isCreatingKycSession ? (
          <Spin indicator={<LoadingOutlined spin />} className="size-5.5" />
        ) : (
          <RiInformationFill size={22} color="var(--color-red-600)" />
        )}
        <span className="hidden md:inline">{t('verify_kyc')}</span>
      </Button>
    </ConfigProvider>
  );

  const dailyUsageButton = (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: colors.usageBorder,
        },
        components: {
          Button: {
            defaultBorderColor: colors.usageBorder,
          },
        },
      }}
    >
      <Button
        className="rounded-4xl! text-text-800! p-0! size-9! md:h-10! md:w-auto! md:px-2.5! gap-1.5!"
        onClick={() => setOpenModal('usage')}
      >
        <AppProgressBarCircle percent={maxUsedPercent ?? 20} strokeWidth={12} />
        <p className="hidden md:block capitalize!">{t('daily_usage_limit')}</p>
      </Button>
    </ConfigProvider>
  );

  return (
    <>
      {/* only display when there is a workspace rate limit */}
      {hasWorkspaceRateLimits && (
        <>
          {maxUsedPercent >= 100 ? null : dailyUsageButton}
          <UsageLimitModal open={openModal === 'usage'} closeModal={() => setOpenModal(null)} />
        </>
      )}

      {/* only display when current workspace kyc is not verified */}
      {workspaceAuthMe?.workspaceAuthMeV2?.data?.id && !isCurrentWorkspaceKycVerified && (
        <>
          {kycBadge}
          <KYCModal open={openModal === 'kyc'} closeModal={() => setOpenModal(null)} sessionUrl={sessionUrl} />
        </>
      )}
    </>
  );
}
